import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import { buildPublicMediaUrl } from "../AttachmentPreview";

const useStyles = makeStyles((theme) => ({
  wrap: {
    width: "100%",
    maxWidth: 270,
    margin: "0 auto",
    padding: theme.spacing(2, 2, 2, 1.5),
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      padding: theme.spacing(2),
    },
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    color: theme.palette.text.secondary,
    marginBottom: 10,
    textTransform: "uppercase",
  },
  phone: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.mode === "dark" ? "#334155" : "#e5e7eb"}`,
    backgroundColor: "#efeae2",
    backgroundImage:
      "radial-gradient(circle at 1px 1px, rgba(15,23,42,0.05) 1px, transparent 0)",
    backgroundSize: "14px 14px",
    minHeight: 300,
  },
  header: {
    backgroundColor: "#075e54",
    color: "#fff",
    padding: "10px 12px",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: "#d1d5db",
    flexShrink: 0,
  },
  headerName: {
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.15,
  },
  headerStatus: {
    fontSize: 11,
    opacity: 0.85,
  },
  thread: {
    padding: 12,
    minHeight: 210,
  },
  day: {
    display: "inline-block",
    backgroundColor: "#fff",
    color: "#54656f",
    fontSize: 11,
    borderRadius: 8,
    padding: "2px 8px",
  },
  dayWrap: {
    textAlign: "center",
    marginBottom: 10,
  },
  bubble: {
    maxWidth: "88%",
    marginLeft: "auto",
    backgroundColor: "#d9fdd3",
    color: "#111b21",
    borderRadius: "8px 8px 2px 8px",
    padding: "6px 8px 4px",
    fontSize: 13,
    lineHeight: 1.35,
    boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  media: {
    width: "100%",
    maxHeight: 140,
    objectFit: "cover",
    borderRadius: 8,
    display: "block",
    marginBottom: 6,
    backgroundColor: "#111",
  },
  fileChip: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    backgroundColor: "rgba(17,27,33,0.06)",
    borderRadius: 8,
    padding: "6px 8px",
    marginBottom: 6,
    fontSize: 12,
    fontWeight: 600,
  },
  time: {
    display: "block",
    textAlign: "right",
    fontSize: 10,
    color: "#667781",
    marginTop: 4,
  },
  buttons: {
    maxWidth: "88%",
    marginLeft: "auto",
    marginTop: 6,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  buttonRow: {
    display: "flex",
    gap: 4,
  },
  action: {
    flex: 1,
    minWidth: 0,
    border: 0,
    borderRadius: 8,
    background: "#fff",
    color: "#168acd",
    fontWeight: 700,
    fontSize: 13,
    padding: "8px 10px",
    cursor: "default",
    boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  token: {
    display: "inline",
    backgroundColor: "#dbeafe",
    color: "#1d4ed8",
    borderRadius: 4,
    padding: "0 3px",
    fontWeight: 700,
    fontSize: 12,
  },
  hint: {
    marginTop: 8,
    fontSize: 11,
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
}));

const renderMessage = (message, tokenClass, highlightVars) => {
  const text = (message || "").trim() || "Tu mensaje aparecerá aquí.";
  if (!highlightVars) return text;
  const parts = text.split(/(\{\{[^}]+\}\})/g);
  return parts.map((part, index) => {
    const match = part.match(/^\{\{([^}]+)\}\}$/);
    if (!match) return part;
    return (
      <span key={`${match[1]}-${index}`} className={tokenClass}>
        {match[1]}
      </span>
    );
  });
};

const buttonRows = value => {
  const source = Array.isArray(value) ? value : [];
  return source
    .map(row => (Array.isArray(row) ? row : [row]).filter(button => button && (String(button.text || "").trim() || String(button.url || "").trim())))
    .filter(row => row.length);
};

const WhatsAppChatPreview = ({
  connectionName,
  message,
  time,
  attachment,
  mediaPath,
  mediaName,
  showVariablesHint = true,
  hint,
  channel = "whatsapp",
  buttons,
}) => {
  const classes = useStyles();
  const now = time || new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const [blobUrl, setBlobUrl] = useState("");

  useEffect(() => {
    if (!attachment) {
      setBlobUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(attachment);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [attachment]);

  const previewUrl = blobUrl || buildPublicMediaUrl(mediaPath);
  const fileLabel = attachment?.name || mediaName || "";
  const mime = attachment?.type || "";
  const isImage =
    mime.startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|bmp)$/i.test(fileLabel || mediaPath || "");
  const isVideo =
    mime.startsWith("video/") || /\.(mp4|webm|mov|3gp)$/i.test(fileLabel || mediaPath || "");
  const hasMedia = Boolean(previewUrl && (attachment || mediaPath));

  const rows = buttonRows(buttons);
  const isTelegram = channel === "telegram";
  const hintText = useMemo(() => {
    if (hint) return hint;
    if (isTelegram) {
      return rows.length
        ? "Así se verá en el grupo o canal: el mensaje y, debajo, los botones."
        : "Así se verá el mensaje en el grupo o canal. Los botones que agregues aparecen debajo.";
    }
    if (showVariablesHint) {
      return "Las variables aparecen como marcadores; el valor real se sustituye en el momento del envío.";
    }
    return "Así se verá el mensaje en el grupo. Esta vista no usa variables.";
  }, [hint, showVariablesHint, isTelegram, rows.length]);

  return (
    <Box className={classes.wrap}>
      <Typography className={classes.label}>
        <span role="img" aria-hidden>👁</span> Vista previa
      </Typography>
      <Box className={classes.phone}>
        <Box className={classes.header} style={isTelegram ? { backgroundColor: "#229ED9" } : undefined}>
          <Box className={classes.avatar} />
          <Box>
            <Typography className={classes.headerName}>
              {connectionName || "Conexión"}
            </Typography>
            <Typography className={classes.headerStatus}>{isTelegram ? "grupo o canal" : "en línea"}</Typography>
          </Box>
        </Box>
        <Box className={classes.thread}>
          <Box className={classes.dayWrap}>
            <span className={classes.day}>Hoy</span>
          </Box>
          <Box className={classes.bubble}>
            {hasMedia && isImage ? (
              <img src={previewUrl} alt={fileLabel || "adjunto"} className={classes.media} />
            ) : null}
            {hasMedia && isVideo ? (
              <video src={previewUrl} className={classes.media} controls />
            ) : null}
            {hasMedia && !isImage && !isVideo ? (
              <Box className={classes.fileChip}>
                <InsertDriveFileIcon fontSize="small" />
                <span>{fileLabel || "Archivo adjunto"}</span>
              </Box>
            ) : null}
            {renderMessage(message, classes.token, showVariablesHint)}
            <span className={classes.time}>{now}</span>
          </Box>
          {rows.length > 0 && (
            <Box className={classes.buttons}>
              {rows.map((row, rowIndex) => (
                <Box key={rowIndex} className={classes.buttonRow}>
                  {row.map((button, buttonIndex) => (
                    <button
                      key={`${rowIndex}-${buttonIndex}`}
                      type="button"
                      className={classes.action}
                      title={button.url || ""}
                    >
                      {String(button.text || "").trim() || "Botón"}
                    </button>
                  ))}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
      <Typography className={classes.hint}>{hintText}</Typography>
    </Box>
  );
};

export const firstCampaignMessage = (values = {}, messageTab = 0) => {
  const current = values[`message${messageTab + 1}`];
  if (current && String(current).trim()) return current;
  return ["message1", "message2", "message3", "message4", "message5"]
    .map((key) => values[key])
    .find((value) => value && String(value).trim()) || "";
};

export default WhatsAppChatPreview;
